import React, { useEffect, useMemo, useState } from 'react';

import { useToast } from '@chakra-ui/react';
import { MeContext } from '../../../context/MeContext';
import CustomToast from '../../../common/CustomToast';
import { useNavigate } from 'react-router';
import { useReportAndToastApiError } from '../../../hooks/useReportAndToastApiError';
import PassTemplatesList from './PassTemplatesList';

import { useDispatch, useSelector } from 'react-redux';
import {
  selectPassTemplates,
  selectFilterValues,
  selectSortValues,
  updatePassTemplates,
  incrementPageIndex,
  resetPageIndex as resetPageIndexAction,
  initialPassTemplates,
} from '../../../store/redux/slices/templateList.slice';

import { useListPassTemplatesQuery } from '../../../store/redux/api/templateApi';
import { normalizedData } from './utils/templatePreviewData';

interface MeContextType {
  state: {
    filterEntity?: { uuid?: string } | null;
    entities?: Array<{ uuid?: string; name?: string }>;
  };
  updateFilterEntity?: (
    entity: { uuid?: string; name?: string } | null
  ) => void;
  isOrganisationActive?: () => boolean;
  isSetUpConfirmed?: () => boolean;
}

function PassTemplatesListWrapper() {
  const toast = useToast();
  const navigate = useNavigate();
  const reportAndToastApiError = useReportAndToastApiError();

  const meCtx = React.useContext(MeContext) as MeContextType;
  const isOrganisationActive = meCtx?.isOrganisationActive() ?? true;
  const isSetUpConfirmed = meCtx?.isSetUpConfirmed() ?? true;
  const { filterEntity, entities } = meCtx.state;
  const updateFilterEntity = meCtx.updateFilterEntity;

  const dispatch = useDispatch();
  const passTemplates = useSelector(selectPassTemplates);
  const filterValues = useSelector(selectFilterValues);
  const sortValues = useSelector(selectSortValues);

  const { items, included, hasMore, status, pageIndex, pageSize } =
    passTemplates;

  const [query, setQuery] = useState('');
  const [inputSearch, setInputSearch] = useState('');

  const prevEntityUuidRef = React.useRef(filterEntity?.uuid);
  const entityChangedRef = React.useRef(false);
  const pollingCountRef = React.useRef(0);
  const MAX_POLLING_ATTEMPTS = 5;

  useEffect(() => {
    const savedEntityUuid = sessionStorage.getItem(
      'passTemplateDuplicateFilterEntityUuid'
    );
    if (savedEntityUuid && updateFilterEntity && entities?.length) {
      sessionStorage.removeItem('passTemplateDuplicateFilterEntityUuid');
      const entity = entities.find(
        (e) =>
          String(e?.uuid) === savedEntityUuid ||
          String((e as { id?: string })?.id) === savedEntityUuid
      );
      if (entity) {
        updateFilterEntity(entity);
      }
    }
  }, [entities, updateFilterEntity]);

  useEffect(() => {
    if (sessionStorage.getItem('passTemplateDuplicateSuccess') === 'true') {
      sessionStorage.removeItem('passTemplateDuplicateSuccess');
      toast({
        render: ({ onClose }) => (
          <CustomToast
            status="success"
            title="Pass template copied successfully"
            onClose={onClose}
          />
        ),
      });
    }
  }, [toast]);

  useEffect(() => {
    const queryPassType =
      filterValues.passType !== ''
        ? '&templateType=' + filterValues.passType
        : '';
    const queryEvent =
      filterValues.event !== '' ? '&eventName=' + filterValues.event : '';
    const queryStatus =
      filterValues.status !== '' ? '&status=' + filterValues.status : '';
    const querySearch = inputSearch !== '' ? '&search=' + inputSearch : '';
    const q = `${queryPassType}${queryEvent}${queryStatus}${querySearch}`;
    setQuery(q);
    dispatch(updatePassTemplates(initialPassTemplates));
    dispatch(resetPageIndexAction());
  }, [filterValues, inputSearch, sortValues]);

  useEffect(() => {
    const entityChanged = prevEntityUuidRef.current !== filterEntity?.uuid;
    prevEntityUuidRef.current = filterEntity?.uuid;

    if (entityChanged) {
      entityChangedRef.current = true;
      dispatch(
        updatePassTemplates({
          ...initialPassTemplates,
          items: [],
          loading: true,
        })
      );
    }
    dispatch(resetPageIndexAction());
  }, [filterEntity, dispatch]);

  const queryArgs = {
    entityId: filterEntity?.uuid,
    // One growing page keeps every loaded card in the subscribed cache entry, so
    // a publish/delete invalidation refetches all of them, not just the last slice.
    page: 1,
    perPage: pageSize * (pageIndex + 1),
    templateType: filterValues.passType || undefined,
    eventName: filterValues.event || undefined,
    // The API returns published only unless statuses are named explicitly.
    status: filterValues.status || 'draft,published',
    search: inputSearch || undefined,
    ...(sortValues.sortBy && sortValues.sortOrder
      ? {
          sortBy: sortValues.sortBy,
          sortOrder: sortValues.sortOrder,
        }
      : {}),
    includeDynamicForm: true,
  };

  const {
    data: apiResult,
    isFetching,
    isError,
    error,
    refetch,
  } = useListPassTemplatesQuery(queryArgs, {
    skip: !isOrganisationActive || !isSetUpConfirmed,
    refetchOnMountOrArgChange: true,
  });

  useEffect(() => {
    if (!isOrganisationActive || !isSetUpConfirmed) return;

    const getData = async () => {
      dispatch(
        updatePassTemplates({
          ...passTemplates,
          loading: true,
          status: 'pending',
        })
      );

      if (isFetching) {
        return;
      }

      if (isError) {
        reportAndToastApiError(error);
        navigate('/pass-templates', { replace: true });
        return;
      }

      if (apiResult) {
        const total = apiResult?.meta?.totalCount ?? 0;
        const data = apiResult?.data ?? [];
        const included = apiResult?.included ?? [];
        const pageCount = Math.ceil(total / pageSize);
        const more =
          pageCount === -1 ? pageIndex >= pageSize : pageIndex < pageCount - 1;

        if (entityChangedRef.current) {
          entityChangedRef.current = false;
        }

        dispatch(
          updatePassTemplates({
            ...passTemplates,
            items: data,
            included,
            total,
            hasMore: more,
            pageCount,
            loading: false,
            status: 'resolved',
          })
        );
      }
    };

    getData();
  }, [
    isFetching,
    query,
    pageIndex,
    pageSize,
    isOrganisationActive,
    isSetUpConfirmed,
    filterEntity,
    apiResult,
  ]);

  // Auto-refetch if any templates are processing
  useEffect(() => {
    if (!apiResult?.data || !isOrganisationActive || !isSetUpConfirmed) {
      return;
    }
    const hasProcessingTemplates = apiResult.data.some(
      (item) => item.attributes?.processingStatus === 'processing'
    );
    if (!hasProcessingTemplates) {
      pollingCountRef.current = 0;
      return;
    }
    if (pollingCountRef.current >= MAX_POLLING_ATTEMPTS) {
      return;
    }
    const intervalId = setInterval(() => {
      pollingCountRef.current += 1;
      if (pollingCountRef.current >= MAX_POLLING_ATTEMPTS) {
        clearInterval(intervalId);
        return;
      }
      refetch();
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [apiResult, refetch, isOrganisationActive, isSetUpConfirmed]);

  const formattedData = useMemo(() => {
    if (entityChangedRef.current && status === 'pending' && items.length > 0) {
      return [];
    }
    return normalizedData(items, included);
  }, [items, included, status]);

  const filtersApplied =
    filterValues.passType !== '' ||
    filterValues.event !== '' ||
    filterValues.status !== '' ||
    inputSearch !== '';

  // isFetching flips true on click before the effect above has set status to
  // 'pending', and flips false one render before that effect commits `items` —
  // OR both so loading covers the full span.
  const isLoadingTemplates = isFetching || status === 'pending';

  return (
    <PassTemplatesList
      items={formattedData}
      isLoading={isLoadingTemplates && pageIndex === 0}
      isLoadingMore={isLoadingTemplates && pageIndex > 0}
      hasMore={hasMore}
      loadMore={() => dispatch(incrementPageIndex())}
      filtersApplied={filtersApplied}
      inputSearch={inputSearch}
      setInputSearch={setInputSearch}
    />
  );
}

export default PassTemplatesListWrapper;
